/* eslint-disable max-lines -- one declarative run→blocks adapter: the delegation/plan/verdict handling
   (ORIONIQ-1055) and the tool-sources descriptor (ORIONIQ-1018) each add a small, cohesive branch; splitting
   the single forEach pass across files would hurt readability more than the length does. */
import type {
  ChatBlock,
  ChatMessage,
  ChatMode,
  ChatStatus,
  ChatToolCall,
  ChatToolKind,
  AgentVerdict,
  RunIncompleteReason,
} from '@logz-pkg/models';
import type { IChatContentItem, IFunctionCallItem } from './general-agent.types';

const INTEGRATION_FUNCTIONS = new Set(['execute_api_read', 'list_available_connections']);
const OIHV_PROFILE = 'oihv';

// The structured RCA answer (AI-service contract 4.0.0), recognised by its OWN `profile: 'oihv'`, the answer
// object's first key. The event that carries it is not a reliable signal: a sub-agent's answer is forwarded
// to the General Agent without the event-level `profile`. orioniq-be serves the object as JSON text.
const isOihvAnswerText = (text: unknown): text is string => {
  if (typeof text !== 'string' || !text.trimStart().startsWith('{')) return false;

  try {
    const parsed: unknown = JSON.parse(text);

    return parsed !== null && typeof parsed === 'object' && (parsed as { profile?: unknown }).profile === OIHV_PROFILE;
  } catch {
    return false;
  }
};

// The run's own answer: an oihv answer on a top-level step (no sub-agent).
const isRunAnswer = (item: IChatContentItem): boolean =>
  !item.subagent && !item.sub_session_id && isOihvAnswerText(item.rational);
const SUBAGENT_FUNCTIONS = new Set(['task', 'invoke_ai_agent']);

// A delegation's closing `agent_output` (the structured verdict) → pass a plain object through as `AgentVerdict`
// (render discriminates the 3 profiles by structure); reject non-objects/arrays. Not redacted (prose, not secrets).
const toVerdict = (agentOutput: unknown): AgentVerdict | undefined =>
  agentOutput !== null && typeof agentOutput === 'object' && !Array.isArray(agentOutput)
    ? (agentOutput as AgentVerdict)
    : undefined;

// Param name / nested object key whose value is a credential — masked before it leaves the adapter (a secret
// never reaches the package or the DOM). Name/key matching only; defense-in-depth (the sample templates secrets).
const SENSITIVE_KEY = /authorization|token|secret|password|api[-_]?key/i;
const REDACTED = '•••';

// Mask nested object keys matching SENSITIVE_KEY (so `{ Authorization: 'Bearer …' }` → `{ Authorization: '•••' }`).
const redactObject = (value: Record<string, unknown>): Record<string, unknown> =>
  Object.fromEntries(Object.entries(value).map(([key, val]) => [key, SENSITIVE_KEY.test(key) ? REDACTED : val]));

// Coerce an array element to string: object → JSON.stringify(redactObject) (not `[object Object]`), else String.
const stringifyArrayElement = (element: unknown): string => {
  if (typeof element === 'string') return element;

  if (element !== null && typeof element === 'object')
    return JSON.stringify(redactObject(element as Record<string, unknown>));

  return String(element);
};

// Coerce a wire param value to the block's `string | string[]`, redacting secrets by name/key (objects → redacted JSON).
const stringifyAndRedact = (
  name: string,
  value: string | string[] | Record<string, unknown> | unknown[] | undefined,
): string | string[] => {
  if (SENSITIVE_KEY.test(name)) return REDACTED;

  if (Array.isArray(value)) return value.map(stringifyArrayElement);

  if (value !== null && typeof value === 'object') return JSON.stringify(redactObject(value));

  return value === null || value === undefined ? '' : String(value);
};

// Redact + stringify each Bedrock `input` value (pre-redacted strings to the renderer — no raw object → no leak / no [object Object]).
const redactInput = (input: Record<string, unknown>): Record<string, string | string[]> =>
  Object.fromEntries(
    Object.entries(input).map(([key, val]) => [key, stringifyAndRedact(key, val as Record<string, unknown>)]),
  );

// Owner bucket for a plan with no sub_session_id (the parent agent's own plan).
const PARENT_OWNER = '__parent__';
const TODO_STATUSES = new Set(['pending', 'in_progress', 'completed']);

type Todo = NonNullable<ChatToolCall['todos']>[number];

// Extract the write_todos `todos` param (an array of `{content,status}` objects on the wire) into the
// typed checklist: drop malformed entries, coerce an unknown status to 'pending'. Pure; the content is
// plan prose (not a credential), so it is NOT redacted. Returns undefined when there's no usable list.
const extractTodos = (functionCall: IFunctionCallItem): Todo[] | undefined => {
  const value = functionCall.parameters?.find(param => param.name === 'todos')?.value;

  if (!Array.isArray(value)) return undefined;

  // `value` narrows to `string[] | unknown[]`; treat as `unknown[]` so the element type-guard is valid.
  const todos: Todo[] = (value as unknown[])
    .filter(
      (entry): entry is Record<string, unknown> =>
        entry !== null && typeof entry === 'object' && typeof (entry as Record<string, unknown>).content === 'string',
    )
    .map(entry => ({
      content: entry.content as string,
      status: (TODO_STATUSES.has(entry.status as string) ? entry.status : 'pending') as Todo['status'],
    }));

  return todos.length ? todos : undefined;
};

const PLAN_FUNCTION = 'write_todos';

const functionName = (functionCall: IFunctionCallItem): string => functionCall.function ?? functionCall.name ?? '';

// Block-level delegation context, stamped on EVERY emitted block (thinking included) from its source item.
// The GROUPING source of truth (the package's process-view builder reads it to group reasoning-only items by
// delegation); ChatToolCall carries the same for rendering — the adapter is the sole writer of both. Absent
// keys are omitted so untouched blocks (the 1:1 path / Builder) stay context-free.
const blockContext = (
  stepIndex: number,
  subSessionId?: string | null,
  subAgent?: string | null,
): Pick<ChatBlock, 'subSessionId' | 'subAgent' | 'stepIndex'> => ({
  stepIndex,
  ...(subSessionId ? { subSessionId } : {}),
  ...(subAgent ? { subAgent } : {}),
});

export const detectToolKind = (functionCall: IFunctionCallItem, item: IChatContentItem): ChatToolKind => {
  const name = functionName(functionCall);

  if (name === PLAN_FUNCTION) return 'plan';

  if (INTEGRATION_FUNCTIONS.has(name)) return 'integration';

  if (SUBAGENT_FUNCTIONS.has(name) || item.subagent) return 'subagent';

  return 'tool';
};

// One agent run's steps → ordered process blocks: each reasoning string a `thinking` block, each function call
// a kind-typed `tool_call` (stable id), a step's `agent_output` a `verdict` block. A `type:'tool'` result pairs
// to its call via a per-run FIFO queue that RESETS each call step, so a resultless call (write_todos/invoke) is
// dropped not mis-paired; params coerced by `stringifyAndRedact`. No wire `tool_call_id` → pairing is by order.
export const chatContentToProcessBlocks = (items: IChatContentItem[]): ChatBlock[] => {
  const blocks: ChatBlock[] = [];
  let previousReasoning: string | undefined;
  const pending: ChatToolCall[] = [];
  // Repeated write_todos calls each send the FULL evolving list → collapse to ONE plan per owner (parent vs each
  // sub_session_id; latest wins), appended after the steps, never inline (package shows one pinned checklist).
  const plansByOwner = new Map<string, ChatToolCall>();
  const planOrder = new Map<string, number>();
  // Counts write_todos TURNS per owner → the step count + the "+N plan updates" affordance; stamped as planUpdateCount (Stage-4 ④).
  const planTurnsByOwner = new Map<string, number>();
  // Verdict blocks collected here (loop-time block-context preserved) and appended LAST — see the append note.
  const verdicts: ChatBlock[] = [];

  items.forEach((item, itemIndex) => {
    // A `subagent_result` is the `task` CLOSE (echoes the task call + the leaf's result text) — the boundary
    // is already emitted by the `agent_delegation` item + the leaf's work is its own steps, so fold it away.
    if (item.type === 'subagent_result') return;

    // A `type:'tool'` event is a tool RESULT (text) + optionally the tool-sources descriptor (`source` /
    // `success`, ORIONIQ-1018). Enter on the type alone — not `&& item.text` — so a result that carries a
    // source but no text (or only the descriptor) still pairs to its call and isn't lost. The descriptor
    // rides this same event; `source == null` is a non-datasource tool and is simply skipped (left unset).
    if (item.type === 'tool') {
      const toolCall = pending.shift();

      if (toolCall) {
        // Keep the dedupe: a result that just echoes the prior reasoning isn't a real output — still
        // consume the queue slot (the call simply carries no result).
        if (item.text && item.text !== previousReasoning) toolCall.result = item.text;

        if (item.source) toolCall.source = item.source;

        if (typeof item.success === 'boolean') toolCall.success = item.success;
      }

      return;
    }

    // A write_todos step renders NO thinking row — its backend `rational` is a bare "Calling: write_todos"
    // and the pinned checklist is the plan surface (ORIONIQ-1055 Stage-4 ④). Count the turn per owner (for
    // the step count + the "+N plan updates" affordance) and skip the thinking block.
    const isPlanStep = item.function_calls?.some(call => functionName(call) === PLAN_FUNCTION);

    if (isPlanStep) {
      const planOwnerKey = item.sub_session_id ?? PARENT_OWNER;

      planTurnsByOwner.set(planOwnerKey, (planTurnsByOwner.get(planOwnerKey) ?? 0) + 1);
    } else if (item.rational && !isRunAnswer(item)) {
      // The run's OWN oihv answer is not a reasoning step: it is rendered once, as the reply, from `agentOutput`.
      // A sub-agent's oihv answer stays a step of its delegation, where the chat renders it inside "How I got
      // here" (orion-chat's `synthesisRenderer`).
      blocks.push({
        type: 'thinking',
        data: item.rational,
        ...blockContext(itemIndex, item.sub_session_id, item.subagent),
      });
      previousReasoning = item.rational;
    }

    // A step's structured `agent_output` (the closing verdict) → collected as a `verdict` block, appended last.
    const verdict = toVerdict(item.agent_output);

    if (verdict) {
      verdicts.push({ type: 'verdict', data: verdict, ...blockContext(itemIndex, item.sub_session_id, item.subagent) });
    }

    // Empty `function_calls: []` survives the backend filter (`!![]` is truthy) but must be a clean no-op
    // — never touch the queue for it.
    if (!item.function_calls?.length) return;

    // A step's results arrive immediately after it (before the next call step), so reset the pending queue
    // on each new call step — this drops a prior step's calls that never emit a `type:'tool'` result (e.g.
    // write_todos / invoke_ai_agent), so the next result can't mis-pair onto them.
    pending.length = 0;

    item.function_calls.forEach((functionCall, functionCallIndex) => {
      const kind = detectToolKind(functionCall, item);

      // A plan step never renders inline + never enters the result-pairing queue (it emits no result).
      // Collapse it per owner: the latest write_todos for a given sub_session_id (or the parent) wins.
      if (kind === 'plan') {
        const ownerKey = item.sub_session_id ?? PARENT_OWNER;
        const todos = extractTodos(functionCall);

        plansByOwner.set(ownerKey, {
          name: functionCall.function ?? functionCall.name ?? '',
          kind: 'plan',
          id: `chat-content-${itemIndex}-${functionCallIndex}`,
          stepIndex: itemIndex,
          ...(todos ? { todos } : {}),
          ...(item.subagent ? { subAgent: item.subagent } : {}),
          ...(item.sub_session_id ? { subSessionId: item.sub_session_id } : {}),
          ...(typeof item.elapsed_ms === 'number' ? { elapsedMs: item.elapsed_ms } : {}),
        });
        planOrder.set(ownerKey, itemIndex);

        return;
      }

      const toolCall: ChatToolCall = {
        name: functionCall.function ?? functionCall.name ?? '',
        parameters: functionCall.parameters?.map(param => ({
          name: param.name ?? '',
          value: stringifyAndRedact(param.name ?? '', param.value),
        })),
        input: functionCall.input && redactInput(functionCall.input),
        kind,
        id: `chat-content-${itemIndex}-${functionCallIndex}`,
        stepIndex: itemIndex,
        ...(item.subagent ? { subAgent: item.subagent } : {}),
        ...(item.sub_session_id ? { subSessionId: item.sub_session_id } : {}),
        ...(typeof item.elapsed_ms === 'number' ? { elapsedMs: item.elapsed_ms } : {}),
      };

      blocks.push({ type: 'tool_call', data: toolCall, ...blockContext(itemIndex, item.sub_session_id, item.subagent) });
      pending.push(toolCall);
    });
  });

  // Append the collapsed plan(s) at the END, ordered by last-update, so the package renders the
  // most-recently-updated plan as the single v1 pinned checklist (multi-plan deferred).
  [...plansByOwner.keys()]
    .sort((ownerA, ownerB) => (planOrder.get(ownerA) ?? 0) - (planOrder.get(ownerB) ?? 0))
    .forEach(ownerKey => {
      const plan = plansByOwner.get(ownerKey);

      if (plan) {
        blocks.push({
          type: 'tool_call',
          data: { ...plan, planUpdateCount: planTurnsByOwner.get(ownerKey) ?? 1 },
          ...blockContext(plan.stepIndex ?? 0, plan.subSessionId, plan.subAgent),
        });
      }
    });

  // Append verdicts LAST — a mid-stream verdict splits the pre-redesign render; the builder hoists by subSessionId (R2).
  blocks.push(...verdicts);

  return blocks;
};

// On a successful finish the agent stopped acting, so clone the plan block (never mutate) with every todo
// flipped to `completed` — the pinned checklist reads "all done" instead of freezing on the last live status.
const completePlanTodos = (blocks: ChatBlock[]): ChatBlock[] =>
  blocks.map(block => {
    if (block.type !== 'tool_call') return block;

    const toolCall = block.data as ChatToolCall;

    if (toolCall.kind !== 'plan' || !toolCall.todos?.length) return block;

    return { ...block, data: { ...toolCall, todos: toolCall.todos.map(todo => ({ ...todo, status: 'completed' })) } };
  });

// True once a run has emitted a tool-call step — the consumer's live-indicator hand-off keys off this (NOT
// chatContent.length): a reasoning-only poll maps to a lone `thinking` block MessageItem won't render alone,
// so the status stays 'thinking' until the first tool step makes the AgentProcess renderable (no blank gap).
export const hasToolCallStep = (chatContent: IChatContentItem[]): boolean =>
  chatContent.some(item => Boolean(item.function_calls?.length));

// A finished run's total wall-clock isn't persisted, but every chatContent item carries `elapsed_ms` — the ms
// elapsed since the run's stream started, cumulative + monotonic non-decreasing (the backend stamps it on each
// event). So the largest value ≈ the whole run's duration. Used ONLY as the read-back fallback when the consumer
// didn't pass an exact `durationMs` (a live build measures the true wall-clock and passes it — that always wins).
const maxElapsedMs = (chatContent: IChatContentItem[]): number | undefined => {
  const elapsed = chatContent
    .map(item => item.elapsed_ms)
    .filter((ms): ms is number => typeof ms === 'number' && Number.isFinite(ms));

  return elapsed.length ? Math.max(...elapsed) : undefined;
};

interface IBuildRunMessageParams {
  chatContent: IChatContentItem[];
  agentOutput?: string | null;
  runId?: string;
  timestamp: number;
  status: ChatStatus;
  // The user stopped the run mid-flight: render its steps + a stopped marker and DROP `agentOutput` entirely
  // (ORIONIQ-1366 — it's the backend's `last_text`, not a reply). Mutually exclusive with `completedAfterStop`.
  stopped?: boolean;
  // The user pressed Stop but the run had already finished (raced completion): render the full answer + a note
  // acknowledging the stop, instead of the stopped marker. Mutually exclusive with `stopped`.
  completedAfterStop?: boolean;
  // The run ended involuntarily via the stuck-run failsafe (ORIONIQ-1182): append an `incomplete` marker block
  // (analogous to `stopped`) so the run reads as cut-short. `incompleteReason` drives the renderer copy/glyph.
  incomplete?: boolean;
  incompleteReason?: RunIncompleteReason | null;
  // Total wall-clock run duration in ms (send → completion), passed by the consumer on the terminal build so
  // the completed summary can show how long the agent took. Omit on live/in-flight builds. When omitted on a
  // FINISHED run (e.g. a history read-back, which can't measure wall-clock), it's derived from the run's
  // `elapsed_ms` so the "took Xm Ys" survives — the passed value always wins over that fallback.
  durationMs?: number;
  // The chat mode this run was sent in (ORIONIQ-1231) — the package shows a ⚡/✨ glyph. Omit → no glyph.
  mode?: ChatMode;
}

// One agent run as a single assistant message: ordered process blocks (reasoning + kind-typed tool calls)
// followed by the final answer once the run completes. Built purely from {chatContent, agentOutput} so the
// same primitive serves the live poll, a cache, and a future history mount.
export const buildRunMessage = ({
  chatContent,
  agentOutput,
  runId,
  timestamp,
  status,
  stopped = false,
  completedAfterStop = false,
  incomplete = false,
  incompleteReason,
  durationMs,
  mode,
}: IBuildRunMessageParams): ChatMessage => {
  const blocks = chatContentToProcessBlocks(chatContent);
  // A stopped or incomplete run is excluded — its plan was interrupted, not completed.
  const isSuccessfulFinish =
    Boolean(agentOutput) && status !== 'streaming' && status !== 'error' && !stopped && !incomplete;
  const withCompletedPlan = isSuccessfulFinish ? completePlanTodos(blocks) : blocks;
  // A user-stopped run appends NO answer (ORIONIQ-1366): its `agentOutput` is the backend's `last_text` at the
  // moment of the stop — a fragment of an answer or raw reasoning, never a human-readable reply — so the run
  // renders as its steps + the stopped marker only. `completedAfterStop` is NOT `stopped`: that run genuinely
  // finished, so it keeps its full answer.
  const appendsAnswer = Boolean(agentOutput) && status !== 'streaming' && !stopped;
  // Drop the final thinking block matching `agentOutput` (else it double-renders, Stage-4 ⑤); trim-compare tolerates ws drift.
  const trimmedOutput = (agentOutput ?? '').trim();
  const isAnswerDup = (block: ChatBlock): boolean =>
    block.type === 'thinking' && typeof block.data === 'string' && block.data.trim() === trimmedOutput;
  const resolvedBlocks = appendsAnswer ? withCompletedPlan.filter(block => !isAnswerDup(block)) : withCompletedPlan;

  if (appendsAnswer) resolvedBlocks.push({ type: 'markdown', data: agentOutput });

  if (stopped) resolvedBlocks.push({ type: 'stopped', data: {} });
  else if (completedAfterStop) resolvedBlocks.push({ type: 'stopped', data: { completedAfterStop: true } });
  else if (incomplete)
    resolvedBlocks.push({
      type: 'incomplete',
      data: { reason: incompleteReason ?? 'internal_error', hadPartialAnswer: appendsAnswer },
    });

  // Show duration on any run that REACHED A TERMINAL STATE, matching where the completed summary renders it.
  // A stopped run no longer appends an answer (ORIONIQ-1366), but the wall-clock it burned before the stop is
  // still real — its "How far I got" bar shows "· took Xm Ys" exactly like the Incomplete one, rather than the
  // two cut-short states diverging. Fall back to the run's own elapsed_ms when the consumer didn't measure the
  // wall-clock: history read-back on both surfaces, and IQon's live stopped path (which never sets a duration —
  // only its `success` branch does). `maxElapsedMs` self-guards, so a stepless stop still resolves undefined.
  const reachedTerminal = appendsAnswer || (stopped && status !== 'streaming');
  const resolvedDurationMs = durationMs ?? (reachedTerminal ? maxElapsedMs(chatContent) : undefined);

  return {
    id: runId ?? `run-${timestamp}`,
    role: 'assistant',
    timestamp,
    durationMs: resolvedDurationMs,
    mode,
    blocks: resolvedBlocks,
  };
};
